/**
 * 双轨 token 管理：本地账号密码登录 / 飞书 Passgate 登录共用一套 axios，
 * 但分别保存自己的 access + refresh，互不污染。
 *
 * 优先级：当本地 token 存在 → 用本地；否则回落到 Passgate。
 * 用户主动切换登录方式（点 Tab → 登录成功）会清掉另一种轨道，避免双 token 混用。
 */

const LOCAL_ACCESS_KEY = 'simulation_platform_local_access_token';
const LOCAL_REFRESH_KEY = 'simulation_platform_local_refresh_token';

export type LoginKind = 'local' | 'passgate' | null;

/** 本地账号密码登录的 token 存储。 */
export const LocalTokens = {
  save(accessToken: string, refreshToken?: string | null): void {
    localStorage.setItem(LOCAL_ACCESS_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(LOCAL_REFRESH_KEY, refreshToken);
    }
  },
  getAccess(): string | null {
    return localStorage.getItem(LOCAL_ACCESS_KEY);
  },
  getRefresh(): string | null {
    return localStorage.getItem(LOCAL_REFRESH_KEY);
  },
  clear(): void {
    localStorage.removeItem(LOCAL_ACCESS_KEY);
    localStorage.removeItem(LOCAL_REFRESH_KEY);
  },
};

import {
  clearPassgateTokens,
  getPassgateAccessToken,
  getPassgateRefreshToken,
  savePassgateTokens,
} from '@/common/auth/passgateTokens';

/** 通过 ``iss`` 字段判断 token 是否来自本平台（``sim-platform``）。 */
function base64UrlDecode(str: string): string {
  const b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  return atob(padded);
}

function decodePayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(base64UrlDecode(parts[1]));
  } catch {
    return null;
  }
}

export function getCurrentLoginKind(): LoginKind {
  if (LocalTokens.getAccess()) return 'local';
  if (getPassgateAccessToken()) return 'passgate';
  return null;
}

export function getActiveAccessToken(): string | null {
  return LocalTokens.getAccess() || getPassgateAccessToken();
}

export function getActiveRefreshToken(): string | null {
  if (LocalTokens.getAccess()) return LocalTokens.getRefresh();
  return getPassgateRefreshToken();
}

/** 登录时调用：保存当前轨道的 token 并清掉另一轨，避免混用导致 401 抖动。 */
export function setLoginTokens(
  kind: 'local' | 'passgate',
  accessToken: string,
  refreshToken?: string | null,
): void {
  if (kind === 'local') {
    clearPassgateTokens();
    LocalTokens.save(accessToken, refreshToken);
  } else {
    LocalTokens.clear();
    savePassgateTokens(accessToken, refreshToken);
  }
}

export function clearAllTokens(): void {
  LocalTokens.clear();
  clearPassgateTokens();
}

/** 工具：access token 是否即将过期；用于请求前主动续期。
 *
 * 解码失败时**保守地返回 false**（按"未过期"处理）：让请求正常带 token 发出，
 * 由后端 401 走被动 refresh，避免每个请求都强制主动 refresh 引发的级联问题
 * （以及在 family 已轮换时跑出 `redirectToLogin` 的死循环）。
 */
export function isTokenExpired(token: string, bufferSeconds = 60): boolean {
  const payload = decodePayload(token);
  if (!payload) return false;
  const exp = typeof payload.exp === 'number' ? (payload.exp as number) : 0;
  if (!exp) return false;
  return exp * 1000 <= Date.now() + bufferSeconds * 1000;
}

/** 解出 JWT 过期时间（毫秒时间戳）；解析失败或无 exp 返回 null。 */
export function getTokenExpiresAt(token: string | null | undefined): number | null {
  if (!token) return null;
  const payload = decodePayload(token);
  if (!payload) return null;
  const exp = typeof payload.exp === 'number' ? (payload.exp as number) : 0;
  return exp > 0 ? exp * 1000 : null;
}
